import React, { useEffect, useState, useMemo } from 'react';
import { Filter, FilterActions, FilterContent, FilterSelectNative } from '@/components/custom/filter';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import toast from 'react-hot-toast';
import usePermission from '@/hooks/usePermission';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { MockExamsCard } from '@/pages/mock-exams/mock-exams-card';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import { t } from 'i18next';
import { Button } from '@/components/ui/button';
import { CardWrapper } from '@/components/ui/card';

export function MockExams({ courses = [] }) {
  const navigate = useNavigate();
  const client = useClient();
  const playfulStudent = usePlayfulStudent();

  const [mockQuestionBooks, setMockQuestionBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      status: {
        approved: false,
        notRealized: false,
        accepting_answers: false,
        review: false
      }
    }
  });

  useEffect(() => {
    let isMounted = true;

    const fetchBooks = async () => {
      setLoading(true);
      try {
        const { data, error } = await client.fetch('question_books?filter[visibility]=open');
        if (isMounted) {
          if (error) {
            toast.error(t('toast.errorGetSimulations'));
          } else {
            setMockQuestionBooks(data || []);
          }
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          toast.error(t('toast.errorGetSimulations'));
          setLoading(false);
        }
      }
    };

    fetchBooks();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredMockQuestionBooks = useMemo(() => {
    const { course, status } = filters.values;

    const selectedStatus = Object.entries(status)
      .filter(([_, isChecked]) => isChecked)
      .map(([key]) => (key === 'notRealized' ? null : key));

    const allStatusUnchecked = selectedStatus.length === 0;

    return mockQuestionBooks.filter(item => {
      const matchesCourse = course === 'blank' || item['course-ids']?.includes(Number(course));
      const matchesStatus = allStatusUnchecked || selectedStatus.includes(item.status);
      return matchesCourse && matchesStatus;
    });
  }, [mockQuestionBooks, filters.values]);

  const handleFilterStatus = field => {
    filters.setFieldValue(`status.${field}`, !filters.values.status[field]);
  };

  const handleFilterCourse = e => {
    filters.setFieldValue('course', e.target.value);
  };

  const mappedCourses = useMemo(() => courses.map(c => ({ ...c, name: c.title })), [courses]);

  const statusOptions = [
    { label: 'Em análise', value: filters.values.status.review, onChange: () => handleFilterStatus('review') },
    { label: 'Aceitando respostas', value: filters.values.status.accepting_answers, onChange: () => handleFilterStatus('accepting_answers') },
    { label: t('filter.performed'), value: filters.values.status.approved, onChange: () => handleFilterStatus('approved') },
    { label: t('filter.unrealized'), value: filters.values.status.notRealized, onChange: () => handleFilterStatus('notRealized') }
  ];

  return (
    <>
      {!playfulStudent && (
        <Filter>
          <FilterContent>
            <FilterSelectNative
              label={t('filter.labelSelectCourse')}
              value={filters.values.course}
              onChange={handleFilterCourse}
              options={mappedCourses}
            />
            <FilterSelectMulti
              label={t('filter.labelSelectStatus')}
              data={statusOptions}
            />
          </FilterContent>

          <FilterActions>
            {usePermission('Criar simulados', 'Provas e simulados') && (
              <Button
                wide
                type="button"
                onClick={() => navigate('/simulados/criar/curso')}
              >
                {t('button.newSimulation')}
              </Button>
            )}
            {usePermission('Criar simulados', 'Provas e simulados') && (
              <Button
                wide
                type="button"
                onClick={() => navigate('/simulados/gerenciar')}
              >
                {t('button.manageSimulations')}
              </Button>
            )}
          </FilterActions>
        </Filter>
      )}

      {loading ? (
        <Loader />
      ) : filteredMockQuestionBooks.length === 0 ? (
        <EmptyState
          type="date"
          text="Em breve, você terá acesso ao nosso simulado. É a oportunidade perfeita para criar um plano de estudos personalizado só para você. Aguarde um pouquinho!"
        />
      ) : playfulStudent ? (
        // Tema infantil: grid do catálogo de trilha (até 3 por linha).
        <div className="grid animate-fade-in gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredMockQuestionBooks.map(m => (
            <MockExamsCard
              key={m?.id}
              mockQuestionBook={m}
            />
          ))}
        </div>
      ) : (
        <CardWrapper className="animate-fade-in">
          {filteredMockQuestionBooks.map(m => (
            <MockExamsCard
              key={m?.id}
              mockQuestionBook={m}
            />
          ))}
        </CardWrapper>
      )}
    </>
  );
}
